// Ported from packages/desk/src/mobile/MobileHome.tsx (docs/PLAN-2026-09-21-mobile-os-shell.md, step 4). Same file name and props
// as the desk copy, the dk- prefix renamed rdr-, so a fix in one is a copy in the other. Radar never imports @gamo/desk.
/**
 * The phone presenter of the desk (founder, 2026-09-21: "treat it like a mobile operating system home screen rather
 * than a responsive admin page"). One navigation system, not two: the home grid holds the few apps used most, the
 * App Library holds the rest, and the dock holds tools that are none of them (Home, Search, the host's own tool,
 * Profile). The wallpaper runs edge to edge under a darkening layer; every word on it sits on that layer or on glass.
 *
 * Layers, top first: a quick-actions menu, a sheet (App Library, Search, Profile), an opened app, the home screen.
 * The browser's Back closes them in that order before it leaves the page. While a layer is up, what is under it is
 * inert, so a screen reader and the keyboard stay in the layer.
 *
 * Porting note (Radar, step 4): this file reads the window store (`../store`); Radar's shell has the desk's store API,
 * so the copy changes only its imports. Radar's `appById(id)` reads the whole registry, so the apps given here are
 * searched with `appIn`. `mobile.avatar` (Radar's drawn avatar) is a Radar addition, to copy back to the desk.
 */
import { useEffect, useRef, useState } from "react";
import type { RadarAppDefinition } from "../../apps/registry";
import { closeWindow, getState, openWindow, type WindowState } from "../store";
import { AppGrid } from "./AppGrid";
import { AppLibrary } from "./AppLibrary";
import { AppSheet } from "./AppSheet";
import { Dock, type DockSlot } from "./Dock";
import { useSwipe } from "./gestures";
import { Greeting } from "./Greeting";
import { homeApps, initialsOf } from "./layout";
import { useBackToClose } from "./layers";
import { ProfileSheet } from "./ProfileSheet";
import { QuickActions } from "./QuickActions";
import { SearchSheet } from "./SearchSheet";
import { closeMobileSheet, goHome, openMobileSheet, useMobileSheet } from "./store";
import { SystemBar } from "./SystemBar";
import type { RadarMobile, MobileProfileItem } from "./types";
import { Widget } from "./Widget";

export interface MobileHomeProps {
  apps: RadarAppDefinition[];
  windows: Record<string, WindowState>;
  stack: string[];
  title: string;
  user: { name: string; email?: string };
  onSignOut: () => void;
  onExit?: (() => void) | undefined;
  /** The desk's account-menu entries; shown in Profile when the host gives no `mobile.profileItems`. */
  menuItems: Array<{ label: string; run: () => void }>;
  mobile?: RadarMobile | undefined;
}

/** Opens an app on the phone: one app at a time, so whatever else was open closes first. */
export function openOnPhone(def: RadarAppDefinition, launchParams?: Record<string, string>): void {
  for (const w of Object.values(getState().windows)) if (w.appId !== def.id) closeWindow(w.id);
  openWindow({ appId: def.id, title: def.name, defaultBounds: def.defaultBounds, singleton: def.singleton, launchParams });
}

function appIn(apps: RadarAppDefinition[], id: string): RadarAppDefinition | undefined {
  return apps.find((a) => a.id === id);
}

function useWidth(): number {
  const [w, setW] = useState(() => window.innerWidth);
  useEffect(() => {
    const on = () => setW(window.innerWidth);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return w;
}

export function MobileHome({ apps, windows, stack, title, user, onSignOut, onExit, menuItems, mobile = {} }: MobileHomeProps) {
  const sheet = useMobileSheet();
  const width = useWidth();
  const [accentId, setAccentId] = useState<string | null>(null);
  const [menu, setMenu] = useState<{ app: RadarAppDefinition; anchor: HTMLElement } | null>(null);
  const scroller = useRef<HTMLDivElement>(null);

  const visible = stack.filter((id) => !windows[id]?.minimized && appIn(apps, windows[id]?.appId ?? ""));
  const topId = visible.at(-1);
  const top = topId ? windows[topId] : undefined;
  const topDef = top ? appIn(apps, top.appId) : undefined;
  const appOpen = Boolean(top && topDef);

  // An app opening closes any sheet over it, and becomes the app with the accent.
  const topApp = top?.appId ?? null;
  useEffect(() => {
    if (!topApp) return;
    closeMobileSheet();
    setMenu(null);
    setAccentId(topApp);
  }, [topApp]);
  // Back closes the opened app (every window: the phone shows one app at a time) before it leaves the page.
  useBackToClose(appOpen, goHome);
  // Leaving the phone presenter (the window grew past the phone width) leaves no sheet behind for next time.
  useEffect(() => () => closeMobileSheet(), []);

  const open = (a: RadarAppDefinition, params?: Record<string, string>) => {
    setMenu(null);
    closeMobileSheet();
    openOnPhone(a, params);
  };
  const quickActions = (a: RadarAppDefinition) => a.quickActions?.({ launch: (params) => open(a, params) }) ?? [];
  // A long press on an app with no quick actions does nothing: a menu holding only "Open" is a tap with extra steps.
  const showActions = (app: RadarAppDefinition, anchor: HTMLElement) => {
    if (quickActions(app).length > 0) setMenu({ app, anchor });
  };

  const initials = initialsOf(user.name) || "?";
  const profileItems: MobileProfileItem[] = mobile.profileItems ?? menuItems.map((m, i) => ({ id: `menu-${i}`, label: m.label, run: m.run }));
  const widgets = (mobile.widgets ?? []).slice(0, 2);
  const dockActive: DockSlot = sheet === "search" ? "search" : sheet === "profile" ? "profile" : appOpen || sheet ? null : "home";

  // A swipe up on the home screen opens the App Library, once the home screen is scrolled to its end (before that a
  // swipe up is a scroll).
  const swipe = useSwipe((dir) => {
    const el = scroller.current;
    const atEnd = !el || el.scrollTop + el.clientHeight >= el.scrollHeight - 2;
    if (dir === "up" && atEnd) openMobileSheet("library");
  });

  const layerUp = appOpen || sheet !== null || menu !== null;

  return (
    <>
      <div className="rdr-mhome" data-rdr-home="" inert={layerUp} aria-hidden={layerUp || undefined}>
        <div className="rdr-mhome-scrim" aria-hidden="true" />
        <div className="rdr-mhome-scroll" ref={scroller} {...swipe}>
          <SystemBar title={title} subtitle={mobile.subtitle ?? mobile.role ?? initials} initials={initials} avatar={mobile.avatar} bell={mobile.bell} onProfile={() => openMobileSheet("profile")} />
          <Greeting stat={mobile.stat} />
          <AppGrid apps={homeApps(apps)} width={width} accentId={accentId} onOpen={(a) => open(a)} onActions={showActions} onMore={() => openMobileSheet("library")} />
          <div className="rdr-mhome-fill" />
          {widgets.length > 0 && (
            <div className="rdr-mwidgets" data-rdr-widgets="">
              {widgets.map((w) => (
                <Widget key={w.id} widget={w} />
              ))}
            </div>
          )}
          <button type="button" className="rdr-mpull" aria-label="All apps" data-rdr-pull="" onClick={() => openMobileSheet("library")}>
            <span aria-hidden="true" />
          </button>
        </div>
      </div>

      {top && topDef && <AppSheet def={topDef} win={top} onHome={goHome} />}

      <AppLibrary open={sheet === "library"} apps={apps} categories={mobile.categories} onClose={closeMobileSheet} onOpen={(a) => open(a)} onActions={showActions} />
      <SearchSheet open={sheet === "search"} apps={apps} searchers={mobile.searchers ?? []} onClose={closeMobileSheet} onOpenApp={(a) => open(a)} />
      <ProfileSheet
        open={sheet === "profile"}
        name={user.name}
        email={user.email}
        role={mobile.role}
        initials={initials}
        avatar={mobile.avatar}
        items={profileItems}
        appearance={mobile.appearance}
        exitLabel={mobile.exitLabel}
        onExit={onExit}
        onSignOut={onSignOut}
        onClose={closeMobileSheet}
      />

      <Dock
        active={dockActive}
        tool={mobile.dockTool}
        onHome={goHome}
        onSearch={() => (sheet === "search" ? closeMobileSheet() : openMobileSheet("search"))}
        onProfile={() => (sheet === "profile" ? closeMobileSheet() : openMobileSheet("profile"))}
      />

      {menu && <QuickActions name={menu.app.name} icon={menu.app.icon} anchor={menu.anchor} actions={quickActions(menu.app)} onOpen={() => open(menu.app)} onClose={() => setMenu(null)} />}
    </>
  );
}
